import React from 'react';
import { Icon } from 'semantic-ui-react';
import styled from 'styled-components';
import useMicLevel from './useMicLevel';
import { meterDisplayState, METER_SEGMENTS } from './micMeterLogic';

// width:100% + box-sizing guard against being laid out as a flex item if this is
// ever moved inside the .controls row, where it would otherwise collapse to the
// width of its icon and the bar would vanish.
// Ambient, not a feature. A thin strip under the controls that you notice only
// when it stops moving.
const Wrapper = styled.div`
    width: 100%;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const BarRow = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    width: 100%;

    i.icon {
        margin: 0 !important;
        font-size: 0.7rem;
        color: rgba(255, 255, 255, 0.35);
        flex-shrink: 0;
    }
`;

const Segments = styled.div`
    display: flex;
    gap: 2px;
    flex: 1;
    min-width: 0;
`;

const Segment = styled.div.withConfig({
    shouldForwardProp: (prop) => !['lit', 'tone'].includes(prop),
})`
    flex: 1;
    min-width: 0;
    height: 4px;
    border-radius: 1px;
    // Unlit segments stay faintly visible so a dead mic reads as an empty meter
    // rather than as no meter at all.
    background: ${(props) => {
        if (!props.lit) return 'rgba(255, 255, 255, 0.12)';
        if (props.tone === 'hot') return '#ef4444';
        if (props.tone === 'warm') return '#d9a441';
        return '#3f9d78';
    }};
    transition: background 80ms linear;
`;

// Holds one line whether or not a message is showing, so the timer and the
// participant cards below never shift. A card moving under the cursor mid-click
// scores a Murphy question as ignored, which costs 100 points. One line is
// enough: both messages fit the 200px panel without wrapping.
const CaptionSlot = styled.div`
    min-height: 14px;
`;

const Caption = styled.div.withConfig({
    shouldForwardProp: (prop) => prop !== 'tone',
})`
    font-size: 0.65rem;
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: ${(props) => (props.tone === 'warning' ? '#d9a441' : 'rgba(255, 255, 255, 0.4)')};
`;

// The top of the bar runs amber then red, so a healthy speaking level reads as
// green with occasional amber rather than pinning the meter.
const toneFor = (index) => {
    if (index >= METER_SEGMENTS - 2) return 'hot';
    if (index >= METER_SEGMENTS - 4) return 'warm';
    return 'cool';
};

/**
 * Live microphone level for the recording controls.
 *
 * Owns the level hook so that sampling (several times a second) re-renders only
 * this subtree, not the whole recording screen. Memoized for the same reason:
 * the parent re-renders on every interaction timer tick and store update.
 *
 * Reports only. It never blocks or stops a recording.
 */
const MicLevelMeter = ({
    microphoneStream,
    isMicrophoneActive = true,
    recording = false
}) => {
    const { segments, silenceMs, available } = useMicLevel(microphoneStream);

    const mode = meterDisplayState({
        available,
        isMicrophoneActive,
        isRecording: recording,
        silenceMs
    });

    if (mode === 'hidden') {
        return null;
    }

    const litCount = mode === 'muted'
        ? 0
        : Math.max(0, Math.min(METER_SEGMENTS, segments));

    return (
        <Wrapper>
            <BarRow>
                <Icon name={mode === 'muted' ? 'microphone slash' : 'microphone'} />
                <Segments
                    role="meter"
                    aria-label="Microphone input level"
                    aria-valuenow={litCount}
                    aria-valuemin={0}
                    aria-valuemax={METER_SEGMENTS}
                    aria-valuetext={`${litCount} of ${METER_SEGMENTS}`}
                >
                    {Array.from({ length: METER_SEGMENTS }, (_, index) => (
                        <Segment key={index} lit={index < litCount} tone={toneFor(index)} />
                    ))}
                </Segments>
            </BarRow>

            <CaptionSlot>
                {mode === 'muted' && <Caption>Muted</Caption>}
                {mode === 'warning' && (
                    // role="alert" so a screen reader announces it. Without this the
                    // users least able to notice a dead level bar get nothing.
                    <Caption tone="warning" role="alert">
                        No sound from your mic
                    </Caption>
                )}
            </CaptionSlot>
        </Wrapper>
    );
};

export default React.memo(MicLevelMeter);
